import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Link2, Share2, LayoutGrid, Send, Search, Repeat2, ImageDown } from "lucide-react";
import api from "@/lib/api";
import { getInitials, avatarColor } from "@/lib/avatar";
import { shareLink } from "@/lib/navigation";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

// Bottom-sheet share menu for a product or a post.
// kind: "product" | "post"; item: the product or post object.
export default function ShareSheet({ kind, item, onClose }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("menu"); // menu | timeline | chat
  const [caption, setCaption] = useState("");
  const [chats, setChats] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const link = shareLink(kind, item.id);
  const isVerse = kind === "post" && /#ParoleDeVie/i.test(item.text || "");

  const snapshot = kind === "product" ? {
    id: item.id, title: item.title, price: item.price, promo_price: item.promo_price ?? null,
    currency: item.currency, image: (item.images && item.images[0] && item.images[0].url) || item.image || null,
    shop_name: item.shop_name || null, shop_verified: !!item.shop_verified,
  } : null;

  useEffect(() => { if (mode === "chat" && !chats.length) api.get("/chats").then(({ data }) => setChats(data)).catch(() => {}); }, [mode, chats.length]);

  const copyLink = async () => { try { await navigator.clipboard.writeText(link); toast.success(t("share_link_copied")); onClose(); } catch { toast.error(t("s_failed")); } };
  const nativeShare = async () => { try { if (navigator.share) { await navigator.share({ title: kind === "product" ? item.title : "RoyalChat Nova", url: link }); onClose(); } else copyLink(); } catch {} };

  const shareAsCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/social/verse-card/${item.id}`, { responseType: "blob" });
      const file = new File([data], "parole-de-vie.png", { type: "image/png" });
      const text = `${(item.text || "").replace(/#\S+/g, "").replace(/🙏/g, "").trim()}\n\nvia RoyalChat Nova`;
      if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Parole de Vie", text });
        try { const { data: up } = await api.post(`/social/posts/${item.id}/share`); } catch {}
        onClose();
      } else {
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url; a.download = "parole-de-vie.png";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        toast.success(t("share_card_saved"));
        onClose();
      }
    } catch (e) {
      if (!(e && e.name === "AbortError")) toast.error(t("s_failed"));
    } finally { setBusy(false); }
  };

  const toTimeline = async () => {
    setBusy(true);
    try {
      if (kind === "product") await api.post("/social/posts", { product_ref: snapshot, text: caption.trim() });
      else await api.post(`/social/posts/${item.id}/reshare`);
      toast.success(t("share_to_timeline_ok"));
      onClose();
    } catch { toast.error(t("s_failed")); } finally { setBusy(false); }
  };

  const toChat = async (chat) => {
    setBusy(true);
    try {
      if (kind === "product") await api.post("/messages", { chat_id: chat.id, type: "product", product_ref: snapshot, text: caption.trim() || null });
      else await api.post("/messages", { chat_id: chat.id, type: "text", text: `${item.text ? item.text.slice(0, 80) + "\n" : ""}${link}` });
      toast.success(t("share_sent"));
      onClose();
    } catch { toast.error(t("s_failed")); } finally { setBusy(false); }
  };

  const filtered = chats.filter((c) => (c.title || "").toLowerCase().includes(q.toLowerCase()));

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center" data-testid="share-sheet">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-[var(--rc-bg)] border-t sm:border border-[var(--rc-border)] rounded-t-3xl sm:rounded-3xl p-5 rc-pop max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading">{t("share")}</h3>
          <button onClick={onClose} data-testid="share-close" className="h-9 w-9 grid place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-muted)]"><X size={18} /></button>
        </div>

        {mode === "menu" && (
          <div className="space-y-2">
            {isVerse && <Row icon={ImageDown} label={t("share_as_card")} onClick={shareAsCard} testid="share-as-card" />}
            <Row icon={LayoutGrid} label={t("share_to_timeline")} onClick={() => (kind === "product" ? setMode("timeline") : toTimeline())} testid="share-timeline" />
            <Row icon={Send} label={t("share_in_chat")} onClick={() => setMode("chat")} testid="share-in-chat" />
            <Row icon={Link2} label={t("share_copy_link")} onClick={copyLink} testid="share-copy-link" />
            {typeof navigator !== "undefined" && navigator.share && <Row icon={Share2} label={t("share_more")} onClick={nativeShare} testid="share-native" />}
            {kind === "post" && <Row icon={Repeat2} label={t("s_reshare")} onClick={toTimeline} testid="share-reshare" />}
          </div>
        )}

        {mode === "timeline" && (
          <div className="space-y-3">
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={t("share_caption_ph")} data-testid="share-caption"
              className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50 resize-none" rows={3} />
            <button onClick={toTimeline} disabled={busy} data-testid="share-timeline-confirm" className="w-full rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3">{t("share_to_timeline")}</button>
          </div>
        )}

        {mode === "chat" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-xl bg-[var(--rc-surface)] border border-[var(--rc-border)] px-3 py-2">
              <Search size={16} className="text-[var(--rc-muted)]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} data-testid="share-chat-search" className="flex-1 bg-transparent text-sm focus:outline-none" />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1">
              {filtered.map((c) => (
                <button key={c.id} onClick={() => toChat(c)} disabled={busy} data-testid={`share-chat-${c.id}`} className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-[var(--rc-surface)] text-left">
                  <div className="h-10 w-10 rounded-full grid place-items-center text-[var(--rc-gold)] text-sm font-semibold border border-[var(--rc-gold)]/50 overflow-hidden" style={{ backgroundColor: c.avatar ? "var(--rc-bg)" : avatarColor(c.title) }}>
                    {c.avatar ? <img src={c.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(c.title)}
                  </div>
                  <span className="text-sm font-medium truncate">{c.title}</span>
                </button>
              ))}
              {!filtered.length && <div className="text-center text-sm text-[var(--rc-muted)] py-6">{t("no_chats") || "No chats"}</div>}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function Row({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full flex items-center gap-3 p-3 rounded-xl bg-[var(--rc-surface)] hover:border-[var(--rc-gold)]/50 border border-transparent transition-colors text-left">
      <span className="h-10 w-10 grid place-items-center rounded-full bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] shrink-0"><Icon size={18} /></span>
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}
